// src/prototype/components/EAPersonalizationWidget.tsx
// ============================================================
// EA PERSONALIZATION STRENGTH WIDGET - COMPACT v2.2
// UPDATES:
// - Removed "Recommendations are reasonably tailored" message
// - Added 4th progress line for "Initiatives"
// ============================================================

import React from 'react';

interface EAPersonalizationWidgetProps {
  overallPercentage: number;
  profilePercentage: number;
  surveysPercentage: number;
  documentsPercentage: number;
  initiativesPercentage?: number;
  isLoading?: boolean;
}

// Strength levels based on percentage
const getStrengthLevel = (pct: number): { 
  label: string; 
  color: string; 
  bgColor: string;
  barColor: string;
} => {
  if (pct >= 80) return { 
    label: 'Excellent', 
    color: 'text-green-700', 
    bgColor: 'bg-green-100',
    barColor: 'bg-green-500',
  };
  if (pct >= 60) return { 
    label: 'Good', 
    color: 'text-[#18365E]', 
    bgColor: 'bg-[#18365E]/10',
    barColor: 'bg-[#18365E]',
  };
  if (pct >= 40) return { 
    label: 'Moderate', 
    color: 'text-[#FF6E4C]', 
    bgColor: 'bg-[#FF6E4C]/10',
    barColor: 'bg-[#FF6E4C]',
  };
  if (pct >= 20) return { 
    label: 'Weak', 
    color: 'text-amber-600', 
    bgColor: 'bg-amber-100',
    barColor: 'bg-amber-500',
  };
  return { 
    label: 'Minimal', 
    color: 'text-slate-500', 
    bgColor: 'bg-slate-100',
    barColor: 'bg-slate-400',
  };
};

export function EAPersonalizationWidget({
  overallPercentage,
  profilePercentage,
  surveysPercentage,
  documentsPercentage,
  initiativesPercentage = 0,
  isLoading = false,
}: EAPersonalizationWidgetProps) {
  const strength = getStrengthLevel(overallPercentage);

  // Breakdown items - 4 items including Initiatives
  const breakdownItems = [
    { label: 'Profile', value: profilePercentage },
    { label: 'Surveys', value: surveysPercentage },
    { label: 'Docs', value: documentsPercentage },
    { label: 'Initiatives', value: initiativesPercentage },
  ];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-[#e5e7eb] p-3 h-full flex flex-col">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-[#18365E]">
            Personalization Strength
          </span>
          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
            Loading
          </span>
        </div>
        <div className="mb-3">
          <div className="flex items-center gap-2">
            <span className="text-xl font-bold text-[#18365E]/40 tabular-nums">—</span>
            <div className="flex-1 h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
              <div className="h-full w-1/3 rounded-full bg-[#e5e7eb] animate-pulse" />
            </div>
          </div>
        </div>
        <div className="space-y-1">
          {['Profile', 'Surveys', 'Docs', 'Initiatives'].map(label => (
            <div key={label} className="flex items-center justify-between">
              <span className="text-[11px] text-[#717182]">{label}</span>
              <div className="flex items-center gap-1.5">
                <div className="w-12 h-1 bg-[#e5e7eb] rounded-full overflow-hidden">
                  <div className="h-full w-1/2 rounded-full bg-[#e5e7eb] animate-pulse" />
                </div>
                <span className="text-[11px] font-semibold text-[#18365E]/40 tabular-nums w-7 text-right">—</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-md border border-[#e5e7eb] p-3 h-full flex flex-col">
      {/* ROW 1: Header with status chip */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold text-[#18365E]">
          Personalization Strength
        </span>
        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${strength.bgColor} ${strength.color}`}>
          {strength.label}
        </span>
      </div>

      {/* ROW 2: Overall meter */}
      <div className="mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xl font-bold text-[#18365E] tabular-nums">
            {overallPercentage}%
          </span>
          <div className="flex-1 h-2 bg-[#e5e7eb] rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-500 ${strength.barColor}`}
              style={{ width: `${overallPercentage}%` }}
            />
          </div>
        </div>
      </div>

      {/* ROW 3: Compact breakdown - 4 items */}
      <div className="space-y-1">
        {breakdownItems.map(item => (
          <div key={item.label} className="flex items-center justify-between">
            <span className="text-[11px] text-[#717182]">{item.label}</span>
            <div className="flex items-center gap-1.5">
              <div className="w-12 h-1 bg-[#e5e7eb] rounded-full overflow-hidden">
                <div 
                  className={`h-full rounded-full ${
                    item.value >= 70 ? 'bg-green-500' : 
                    item.value >= 40 ? 'bg-[#18365E]' : 
                    item.value > 0 ? 'bg-[#FF6E4C]' :
                    'bg-[#e5e7eb]'
                  }`}
                  style={{ width: `${item.value}%` }}
                />
              </div>
              <span className="text-[11px] font-semibold text-[#18365E] tabular-nums w-7 text-right">
                {item.value}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default EAPersonalizationWidget;